import { useEffect, useMemo, useRef, useState } from 'react'
import { MediaRowFieldsFragment } from '../../graphql'
import throttle from 'lodash.throttle'
import { useInView } from 'react-intersection-observer'
import { useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { selectLazyLoading, setLazyLoading } from '../../store/base/commonSlice'

export const useLazyLoadMediaRows = (
  objectName: string,
  rows: MediaRowFieldsFragment[],
  fetchMore: (data: { variables: { rowsTake: number; rowsSkip: number } }) => Promise<{
    data: { [key: string]: { rows: MediaRowFieldsFragment[] } }
  }>,
  initLoading: boolean,
  take: number,
) => {
  const [skip, setSkip] = useState(0)
  const [loadingMore, setLoadingMore] = useState(false)

  const prevRowCount = useRef(0)
  const prevFilteredRowCount = useRef(0)

  const location = useLocation()

  const dispatch = useDispatch()
  const lazyLoadingState = useSelector(selectLazyLoading)

  const hasMore = useMemo(
    () => lazyLoadingState.find((item) => item.slug === location.pathname)?.hasMore ?? true,
    [lazyLoadingState, location.pathname],
  )

  useEffect(() => {
    const existingLazyLoadingState = lazyLoadingState.find((item) => item.slug === location.pathname)
    if (!existingLazyLoadingState) {
      dispatch(setLazyLoading([...lazyLoadingState, { slug: location.pathname, hasMore: true }]))
    }
  }, [location.pathname])

  useEffect(() => {
    const rowCount = rows.length || 0
    if (
      hasMore === false &&
      rowCount > 0 &&
      rowCount <= take &&
      (prevRowCount.current === 0 || prevRowCount.current > rowCount)
    ) {
      dispatch(
        setLazyLoading(
          lazyLoadingState.map((item) => (item.slug === location.pathname ? { ...item, hasMore: true } : item)),
        ),
      )
    }

    const filteredRows = rows.filter((row) => row.items.list.length > 0) || []

    //if we got more rows, but they're all empty, fetch again
    if (rowCount > prevRowCount.current && filteredRows.length === prevFilteredRowCount.current) {
      loadNewRows()
    }
    prevRowCount.current = rowCount
    prevFilteredRowCount.current = filteredRows.length
  }, [rows, hasMore, location.pathname, lazyLoadingState, dispatch, take])

  const loadNewRows = async () => {
    if (initLoading || loadingMore || !hasMore) {
      return
    }

    setLoadingMore(true)

    try {
      const newSkip = skip + take

      const newRows = await fetchMore({
        variables: {
          rowsTake: take,
          rowsSkip: newSkip,
        },
      })

      const resCount = newRows.data[objectName]?.rows.length || 0

      if (resCount < take) {
        dispatch(
          setLazyLoading(
            lazyLoadingState.map((item) => (item.slug === location.pathname ? { ...item, hasMore: false } : item)),
          ),
        )
      }

      setSkip(newSkip)
    } catch (error) {
      console.error('Failed to fetch more rows:', error)
    } finally {
      setLoadingMore(false)
    }
  }

  const loadNewRowsRef = useRef(loadNewRows)

  useEffect(() => {
    loadNewRowsRef.current = loadNewRows
  }, [loadNewRows])

  const throttledLoadNewRows = useMemo(
    () =>
      throttle(() => {
        loadNewRowsRef.current()
      }, 200),
    [],
  )

  const { ref: inViewRef } = useInView({
    onChange: (inView) => {
      if (inView) {
        throttledLoadNewRows()
      }
    },
  })

  return {
    inViewRef,
    loadingMore,
  }
}
